import { useState } from "react";
import {
  ArrowRight,
  Image,
  Move,
  Settings,
  Clipboard,
  CheckCircle,
  X
} from "react-feather";
import "./PicturesUpload.css";

export default function PicturesUpload({ images, setImages, onNext }) {
  const [showInstructions, setShowInstructions] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [loading, setLoading] = useState(false);

  function handleFiles(selectedFiles) {
    setImages((prev) => {
      const remainingSlots = 1 - prev.length;

      if (selectedFiles.length > remainingSlots) {
        // setModalMessage("Only 2 images are allowed in this version.");
        setModalMessage("Only 1 image is allowed in this beta version.");
        setShowLimitModal(true);
      }

      if (remainingSlots <= 0) return prev;

      const newFiles = Array.from(selectedFiles)
        .filter(file => {
          const valid = ["image/png", "image/jpeg"].includes(file.type);
          if (!valid) {
            setModalMessage("Only PNG and JPG images are allowed.");
            setShowLimitModal(true);
            return false;
          }
          if (file.size > 4 * 1024 * 1024) {
            setModalMessage("Image is too large (maximum size is 4MB). Try uploading a screenshot instead.");
            setShowLimitModal(true);
            return false;
          }
          return valid;
        })
        .slice(0, remainingSlots)
        .map((file) => ({
          file,
          preview: URL.createObjectURL(file)
        }));

      return [...prev, ...newFiles];
    });
  }

  function removeFile(index) {
    setImages((prev) => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[index].preview);
      copy.splice(index, 1);
      return copy;
    });
  }

  function onDrop(e) {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }

  function resizeImage(file, maxWidth = 800, quality = 0.65) {
    return new Promise((resolve) => {
      const img = new window.Image(); // ✅ IMPORTANT FIX
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement("canvas");

        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            resolve(
              new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now()
              })
            );
          },
          "image/jpeg",
          quality
        );
      };
    });
  }


  return (
    <div className="pictures-upload">
      {/* TOP ACTIONS */}

      <div className="upload-header">

        <div className="steps-progress">

          <div className="step active">
            <div className="step-icon">
              <Image size={18} />
            </div>
            <span>Upload Picture</span>
          </div>

          <div className="step-line" />

          <div className="step">
            <div className="step-icon">
              <Move size={18} />
            </div>
            <span>Add Signs</span>
          </div>

          <div className="step-line" />

          <div className="step">
            <div className="step-icon">
              <Settings size={18} />
            </div>
            <span>Project Setup</span>
          </div>

          <div className="step-line" />

          <div className="step">
            <div className="step-icon">
              <Clipboard size={18} />
            </div>
            <span>Project Review</span>
          </div>

          <div className="step-line" />

          <div className="step">
            <div className="step-icon">
              <CheckCircle size={18} />
            </div>
            <span>Results</span>
          </div>

        </div>

        <div className="upload-header-content">

          <div className="upload-title">

            <h2>Upload Pictures</h2>

            <p className="subtitle">
              Upload a photo of <strong>where the signs will be installed</strong>.
            </p>

          </div>

          <div className="actions">

            <button
              className="secondary"
              onClick={() => setShowInstructions(true)}
            >
              Instructions
            </button>

            <button
              className="primary"
              disabled={images.length === 0 || loading}
              onClick={async () => {

                try {

                  setLoading(true);

                  const resized = await Promise.all(
                    images.map(async (img) => {

                      const resizedFile = await resizeImage(img.file);

                      return {
                        ...img,
                        file: resizedFile,
                        preview: URL.createObjectURL(resizedFile)
                      };

                    })
                  );

                  setImages(resized);

                  onNext();

                } finally {

                  setLoading(false);

                }

              }}
            >
              <>
                {loading ? (
                  "Preparing image..."
                ) : (
                  <>
                    Next <ArrowRight size={12} />
                  </>
                )}
              </>
            </button>

          </div>

        </div>

      </div>

      {/* PREVIEWS */}
      {images.length > 0 && (
        <div className="preview-grid">
          {images.map((img, index) => (
            <div key={index} className="preview-item">
              <img src={img.preview} alt="preview" />
              <button
                className="remove-btn"
                onClick={() => removeFile(index)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* UPLOAD AREA */}
      <div
        className="upload-box"
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <input
          type="file"
          // multiple
          // accept="image/*"
          accept="image/png, image/jpeg"
          onChange={(e) => handleFiles(e.target.files)}
        />

        <div className="upload-content">
          <span className="upload-icon">＋</span>
          <p>Drag & drop your image here</p>
          <p style={{ fontSize: "13px", marginTop: "-5px", color: "#999", }}>Picture of the signs location</p>
          <span className="or">or</span>
          <button className="select-btn">Select from your device</button>
        </div>
      </div>

      <footer className="app-footer">
        <div>Powered by <strong>Le3eD Software</strong></div>
        <div>© 2026 Le3eD Software. All rights reserved.</div>
      </footer>


      {/* INSTRUCTIONS MODAL */}
      {showInstructions && (
        <div
          className="modal-overlay"
          onClick={() => setShowInstructions(false)}
        >
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowInstructions(false)}
            >
              <X size={16} />
            </button>

            <div className="instructions-heading">
              <span className="instructions-kicker">5 simple steps</span>
              <h1>How it works</h1>
              <p>From your first photo to a project ready for review.</p>
            </div>

            <div className="instructions-flow" aria-label="How Ai 4 Signs works">
              <div className="instruction-step">
                <div className="instruction-visual upload-visual">
                  <div className="mini-building">
                    <span className="mini-awning" />
                    <span className="mini-window left" />
                    <span className="mini-window right" />
                    <span className="mini-door" />
                  </div>
                  <div className="floating-camera"><Image size={25} /></div>
                  <span className="spark spark-one" />
                </div>
                <div className="instruction-number">01</div>
                <h3>Upload photos</h3>
                <p>Upload clear photos of the area.</p>
              </div>

              <div className="instruction-connector"><span /></div>

              <div className="instruction-step">
                <div className="instruction-visual area-visual">
                  <div className="area-canvas">
                    <span className="sign-zone zone-one" />
                    <span className="sign-zone zone-two" />
                    <span className="sign-zone zone-three" />
                    <span className="cursor-arrow">↖</span>
                  </div>
                </div>
                <div className="instruction-number">02</div>
                <h3>Place sign areas</h3>
                <p>Mark exactly where signs will go.</p>
              </div>

              <div className="instruction-connector"><span /></div>

              <div className="instruction-step">
                <div className="instruction-visual types-visual">
                  <div className="config-window">
                    <div className="config-top"><i /><i /><i /></div>
                    <div className="config-body">
                      <div className="config-preview"><span /><span /></div>
                      <div className="config-options"><i /><i /><i /></div>
                    </div>
                    <div className="config-submit">SUBMIT</div>
                  </div>
                </div>
                <div className="instruction-number">03</div>
                <h3>Choose sign types</h3>
                <p>Pick materials, lighting, and upload your logo.</p>
              </div>

              <div className="instruction-connector"><span /></div>

              <div className="instruction-step">
                <div className="instruction-visual review-visual">
                  <div className="review-sheet">
                    <span className="sheet-line wide" />
                    <span className="sheet-line" />
                    <span className="sheet-line short" />
                    <CheckCircle size={28} />
                  </div>
                  <div className="review-check"><CheckCircle size={18} /></div>
                </div>
                <div className="instruction-number">04</div>
                <h3>Review & submit</h3>
                <p>See mockups, pricing, and your proposal.</p>
              </div>

              <div className="instruction-connector"><span /></div>

              <div className="instruction-step">
                <div className="instruction-visual project-review-visual">
                  <div className="review-screen">
                    <div className="review-screen-bar"><i /><i /><i /></div>
                    <div className="review-screen-grid">
                      <span /><span /><span /><span />
                    </div>
                  </div>
                  <div className="review-people">
                    <span className="person person-one" />
                    <span className="person person-two" />
                  </div>
                </div>
                <div className="instruction-number">05</div>
                <h3>Project Review</h3>
                <p>Refine the final details with our team.</p>
              </div>
            </div>

          </div>
        </div>
      )}

      {showLimitModal && (
        <div className="modal-overlay" onClick={() => setShowLimitModal(false)}>
          <div className="modal-card apple" onClick={(e) => e.stopPropagation()}>
            <h3>Alert</h3>
            <p>{modalMessage}</p>

            <button
              className="primary"
              onClick={() => setShowLimitModal(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
